/**
 * Permissions NexusApi — Full Contract
 *
 * Relationship-Based Access Control (ReBAC) endpoints.
 * All inputs validated at controller boundary (Zod .parse()).
 * All outputs typed end-to-end via InferSchemas.
 *
 * Endpoints:
 * - CheckAccess                  POST   /api/permissions/check
 * - GrantPermission              POST   /api/permissions/grant
 * - RevokePermission             POST   /api/permissions/revoke
 * - CalculateRevokeImpact        POST   /api/permissions/revoke-impact
 * - ListAccessibleResources      GET    /api/permissions/accessible/:resourceType
 * - ListResourceAccess           GET    /api/permissions/:resourceType/:resourceId/access
 * - UpdateRelation               PATCH  /api/permissions/:resourceType/:resourceId/access
 * - GrantWildcard                POST   /api/permissions/wildcard   (org:admin only)
 * - GetOrgPermissionSettings     GET    /api/permissions/org-settings
 * - UpdateOrgPermissionSettings  PATCH  /api/permissions/org-settings
 * - UpdateResourceTypeVisibility PATCH  /api/permissions/org-settings/resource-type
 * - GetFeatureAccess             GET    /api/permissions/features
 * - GetGroupFeatureAccess        GET    /api/permissions/groups/:groupId/features
 * - AcknowledgeGroupFeature      POST   /api/permissions/groups/:groupId/features/:featureKey/acknowledge
 * - GetGroupResourceAccess       GET    /api/permissions/groups/:groupId/resources
 */

import { z } from "zod";

import {
  AppFeatureKeySchema,
  GroupFeatureDecisionSchema,
  GroupFeatureStatusSchema
} from "../../../shared/domain/features";
import {
  RELATIONS,
  RESOURCE_TYPES,
  RESOURCE_VISIBILITY_VALUES,
  SUBJECT_TYPES,
  UNLISTED_REACH_VALUES
} from "../../../shared/domain/permissions";
import { ApiSuccessSchema } from "../../common/response.schema";
import type { InferSchemas } from "../../schema-inference";
import { PermissionRecordSchema } from "./schemas/permission-record.schema";

export const ResourceTypeSchema = z.enum(RESOURCE_TYPES);
export const SubjectTypeSchema = z.enum(SUBJECT_TYPES);
export const RelationSchema = z.enum(RELATIONS);
const ResourceIdSchema = z.string().uuid("resourceId must be a valid UUID");
/** Accepts a UUID or the wildcard sentinel '*' — used in revoke endpoints where the grant may be wildcard-scoped. */
const ResourceIdOrWildcardSchema = z.union([
  z.string().uuid("resourceId must be a valid UUID"),
  z.literal("*")
]);
const SubjectIdSchema = z.string().min(1, "subjectId must not be empty");

export const ZPermissions = {
  CheckAccess: {
    method: "POST" as const,
    path: "/api/permissions/check",
    Body: z.object({
      resourceType: ResourceTypeSchema,
      resourceId: ResourceIdSchema,
      requiredRelation: RelationSchema.optional()
    }),
    Response: ApiSuccessSchema(
      z.object({
        hasAccess: z.boolean(),
        userRelation: RelationSchema.nullable()
      })
    )
  },

  GrantPermission: {
    method: "POST" as const,
    path: "/api/permissions/grant",
    Body: z.object({
      resourceType: ResourceTypeSchema,
      resourceId: ResourceIdSchema,
      subjectType: SubjectTypeSchema,
      subjectId: SubjectIdSchema,
      relation: RelationSchema
    }),
    Response: ApiSuccessSchema(
      z.object({
        id: z.string().uuid(),
        createdAt: z.string().datetime()
      })
    )
  },

  CalculateRevokeImpact: {
    method: "POST" as const,
    path: "/api/permissions/revoke-impact",
    Body: z.object({
      resourceType: ResourceTypeSchema,
      resourceId: ResourceIdSchema,
      subjectType: SubjectTypeSchema,
      subjectId: SubjectIdSchema
    }),
    Response: ApiSuccessSchema(
      z.object({
        directCount: z.literal(1),
        downstream: z.array(PermissionRecordSchema)
      })
    )
  },

  RevokePermission: {
    method: "POST" as const,
    path: "/api/permissions/revoke",
    Body: z.object({
      resourceType: ResourceTypeSchema,
      resourceId: ResourceIdOrWildcardSchema,
      subjectType: SubjectTypeSchema,
      subjectId: SubjectIdSchema,
      cascadeSubjectIds: z.array(SubjectIdSchema).optional()
    }),
    Response: ApiSuccessSchema(
      z.object({
        revokedCount: z.number().int().positive()
      })
    )
  },

  ListAccessibleResources: {
    method: "GET" as const,
    path: "/api/permissions/accessible/:resourceType",
    PathVars: z.object({
      resourceType: ResourceTypeSchema
    }),
    Response: ApiSuccessSchema(
      z.object({
        resourceIds: z.array(z.string().uuid())
      })
    )
  },

  ListResourceAccess: {
    method: "GET" as const,
    path: "/api/permissions/:resourceType/:resourceId/access",
    PathVars: z.object({
      resourceType: ResourceTypeSchema,
      resourceId: ResourceIdSchema
    }),
    Response: ApiSuccessSchema(
      z.object({
        permissions: z.array(PermissionRecordSchema),
        /**
         * What reaches this resource WITHOUT appearing in `permissions`. Read it
         * before concluding anything from an empty array: the same `[]` means
         * "the whole organization" under `organization`, "a grant on this type's
         * wildcard" under `type_wide_grant`, and "nobody" only under `nobody`.
         */
        unlistedReach: z.enum(UNLISTED_REACH_VALUES)
      })
    )
  },

  UpdateRelation: {
    method: "PATCH" as const,
    path: "/api/permissions/:resourceType/:resourceId/access",
    PathVars: z.object({
      resourceType: ResourceTypeSchema,
      resourceId: ResourceIdSchema
    }),
    Body: z.object({
      subjectType: SubjectTypeSchema,
      subjectId: SubjectIdSchema,
      relation: RelationSchema
    }),
    Response: ApiSuccessSchema(
      z.object({
        id: z.string().uuid()
      })
    )
  },

  GrantWildcard: {
    method: "POST" as const,
    path: "/api/permissions/wildcard",
    Body: z.object({
      resourceType: ResourceTypeSchema,
      groupId: z.string().uuid("groupId must be a valid UUID"),
      relation: RelationSchema
    }),
    Response: ApiSuccessSchema(
      z.object({
        id: z.string().uuid()
      })
    )
  },

  GetOrgPermissionSettings: {
    method: "GET" as const,
    path: "/api/permissions/org-settings",
    Response: ApiSuccessSchema(
      z.object({
        defaultResourceVisibility: z.enum(RESOURCE_VISIBILITY_VALUES),
        resourceVisibilityByType: z.partialRecord(
          z.enum(RESOURCE_TYPES),
          z.enum(RESOURCE_VISIBILITY_VALUES)
        )
      })
    )
  },

  UpdateOrgPermissionSettings: {
    method: "PATCH" as const,
    path: "/api/permissions/org-settings",
    Body: z.object({
      defaultResourceVisibility: z.enum(RESOURCE_VISIBILITY_VALUES)
    }),
    Response: ApiSuccessSchema(
      z.object({
        defaultResourceVisibility: z.enum(RESOURCE_VISIBILITY_VALUES),
        resourceVisibilityByType: z.partialRecord(
          z.enum(RESOURCE_TYPES),
          z.enum(RESOURCE_VISIBILITY_VALUES)
        )
      })
    )
  },

  UpdateResourceTypeVisibility: {
    method: "PATCH" as const,
    path: "/api/permissions/org-settings/resource-type",
    Body: z.object({
      resourceType: z.enum(RESOURCE_TYPES),
      visibility: z.enum(RESOURCE_VISIBILITY_VALUES).nullable()
    }),
    Response: ApiSuccessSchema(
      z.object({
        defaultResourceVisibility: z.enum(RESOURCE_VISIBILITY_VALUES),
        resourceVisibilityByType: z.partialRecord(
          z.enum(RESOURCE_TYPES),
          z.enum(RESOURCE_VISIBILITY_VALUES)
        )
      })
    )
  },

  /** Returns all features and whether the current user can access each one. */
  GetFeatureAccess: {
    method: "GET" as const,
    path: "/api/permissions/features",
    Response: ApiSuccessSchema(
      z.array(
        z.object({
          key: z.string(),
          accessible: z.boolean()
        })
      )
    )
  },

  /**
   * Returns all features with whether a specific group has explicit VIEWER
   * access to each, and — in `status` — whether a feature it lacks was ever
   * decided by an admin. See `GroupFeatureStatus` for the three values.
   */
  GetGroupFeatureAccess: {
    method: "GET" as const,
    path: "/api/permissions/groups/:groupId/features",
    PathVars: z.object({ groupId: z.string().uuid() }),
    Response: ApiSuccessSchema(
      z.array(
        z.object({
          key: z.string(),
          displayName: z.string(),
          description: z.string(),
          resourceId: z.string().uuid(),
          hasAccess: z.boolean(),
          status: GroupFeatureStatusSchema
        })
      )
    )
  },

  /**
   * An admin's decision on one feature for one group (org:admin only).
   * `grant` writes the group's VIEWER grant; `decline` records that the feature
   * stays withheld. Either clears `new_not_granted`. Declining is refused on a
   * group with no feature grants (nothing is withheld from it) and on a feature
   * the group already holds (revoke it instead).
   */
  AcknowledgeGroupFeature: {
    method: "POST" as const,
    path: "/api/permissions/groups/:groupId/features/:featureKey/acknowledge",
    PathVars: z.object({ groupId: z.string().uuid(), featureKey: AppFeatureKeySchema }),
    Body: z.object({ decision: GroupFeatureDecisionSchema }),
    Response: ApiSuccessSchema(
      z.object({
        key: AppFeatureKeySchema,
        status: GroupFeatureStatusSchema
      })
    )
  },

  /** Returns all configurable resource types and whether the group has wildcard VIEWER access to each. */
  GetGroupResourceAccess: {
    method: "GET" as const,
    path: "/api/permissions/groups/:groupId/resources",
    PathVars: z.object({ groupId: z.string().uuid() }),
    Response: ApiSuccessSchema(
      z.array(
        z.object({
          resourceType: ResourceTypeSchema,
          displayName: z.string(),
          hasWildcardAccess: z.boolean()
        })
      )
    )
  }
} as const;

export type TApiPermissions = InferSchemas<typeof ZPermissions>;
